/**
 * ConfirmActionDialog — shared confirm-with-options dialog for atomic
 * re-analyze / re-scrape actions in the Creator tab.
 *
 * Why a shared component:
 *   Each atomic operation (Re-analyze voice, Re-analyze identity,
 *   Re-generate pillars/examples, Scrape more, Re-scrape profile, Add platform)
 *   needs the same UX shape: title + description + optional model picker
 *   (Gemini-only) + optional maxPosts input + Cancel/Confirm. Inlining this
 *   in 6+ places creates drift; centralizing it keeps re-analyze ergonomics
 *   uniform and trivially extendable (e.g. adding a `temperature` slider).
 *
 * Variants supported via `mode` prop:
 *   - 'ai'              → available model selector visible, Confirm dispatches with model
 *   - 'scrape'          → no model selector (BD has no model choice), maxPosts visible
 *   - 'ai-with-scrape'  → both model selector + maxPosts (initial onboarding,
 *                         redo-url, add-platform)
 *
 * The Confirm callback receives `{ model, maxPosts }` — caller decides which
 * fields to actually send to the backend based on the operation kind.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  BaseDialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
} from '@modules/ui-components';
import { AvailableModelSelector } from '@modules/ai-settings';

const TESTID = 'confirm-action-dialog';

export type ConfirmActionMode = 'ai' | 'scrape' | 'ai-with-scrape';

export interface ConfirmActionResult {
  /** AI model to use (when mode includes 'ai'). undefined for pure-scrape. */
  model?: string;
  /**
   * Max posts to scrape (when mode includes 'scrape'). 'all' for unlimited.
   * undefined for AI-only operations.
   */
  maxPosts?: number | 'all';
  /**
   * Optional user-supplied focus for THIS analysis pass — appears only when
   * mode includes 'ai'. Trimmed; empty/whitespace becomes undefined and the
   * caller can omit it from the request.
   */
  analysisInstructions?: string;
  /**
   * Optional date-range filter (YYYY-MM-DD, inclusive) — appears ONLY for
   * pure mode='scrape' (not 'ai-with-scrape'). Forwarded to BrightData
   * trigger payload. Both fields are independently optional: only-since,
   * only-until, both, or neither.
   */
  scrapeSince?: string;
  scrapeUntil?: string;
}

export interface ConfirmActionDialogProps {
  open: boolean;
  /** Title shown in the dialog header — e.g. "Re-analyze voice for LinkedIn". */
  title: string;
  /** Optional explanation text below the title. */
  description?: string;
  /** Operation kind — determines which controls are visible. */
  mode: ConfirmActionMode;
  /** Project whose connected AI providers should populate the model picker. */
  projectId?: string;
  /** Default model when mode includes 'ai'. Default: 'gemini-3-flash-preview'. */
  defaultModel?: string;
  /** Default maxPosts when mode includes 'scrape'. Default: 10. */
  defaultMaxPosts?: number;
  /** Label override for the max-posts numeric field. */
  maxPostsLabel?: string;
  /** Helper text override under the max-posts controls. */
  maxPostsHelp?: string;
  /** Force-show/hide date range controls for scrape-like actions. */
  showDateRange?: boolean;
  /** Confirm button label override. Default: 'Run' or 'Run with selected model'. */
  confirmLabel?: string;
  /** Called with selected options when user clicks Confirm. */
  onConfirm: (result: ConfirmActionResult) => Promise<void> | void;
  onClose: () => void;
}

import { CONTENT_PLAN_DEFAULT_MODEL } from '../../constants/modelDefaults';

const DEFAULT_GEMINI_MODEL = CONTENT_PLAN_DEFAULT_MODEL;
const DEFAULT_MAX_POSTS = 10;

export const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  open,
  title,
  description,
  mode,
  projectId,
  defaultModel = DEFAULT_GEMINI_MODEL,
  defaultMaxPosts = DEFAULT_MAX_POSTS,
  maxPostsLabel = 'Max posts to scrape',
  maxPostsHelp = 'BrightData scrapes up to N most-recent posts. ~$0.005 per deep-scraped post via the Discover API.',
  showDateRange,
  confirmLabel,
  onConfirm,
  onClose,
}) => {
  const showModel = mode === 'ai' || mode === 'ai-with-scrape';
  const showMaxPosts = mode === 'scrape' || mode === 'ai-with-scrape';
  // Date-range pickers default to pure mode='scrape', but callers can hide
  // them for non-BrightData scrape-like actions such as X API sync.
  const shouldShowDateRange = showDateRange ?? mode === 'scrape';

  const [model, setModel] = useState(defaultModel);
  const [maxPostsNum, setMaxPostsNum] = useState<number>(defaultMaxPosts);
  const [allPosts, setAllPosts] = useState(false);
  const [analysisInstructions, setAnalysisInstructions] = useState('');
  const [scrapeSince, setScrapeSince] = useState('');
  const [scrapeUntil, setScrapeUntil] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog reopens
  React.useEffect(() => {
    if (open) {
      setModel(defaultModel);
      setMaxPostsNum(defaultMaxPosts);
      setAllPosts(false);
      setAnalysisInstructions('');
      setScrapeSince('');
      setScrapeUntil('');
      setSubmitting(false);
      setError(null);
    }
  }, [open, defaultModel, defaultMaxPosts]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result: ConfirmActionResult = {};
      if (showModel) result.model = model;
      if (showMaxPosts) result.maxPosts = allPosts ? 'all' : maxPostsNum;
      if (showModel) {
        const trimmed = analysisInstructions.trim();
        if (trimmed) result.analysisInstructions = trimmed;
      }
      if (shouldShowDateRange) {
        if (scrapeSince) result.scrapeSince = scrapeSince;
        if (scrapeUntil) result.scrapeUntil = scrapeUntil;
      }
      await onConfirm(result);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid={TESTID}
    >
      <DialogHeader title={title} onClose={submitting ? undefined : onClose} />
      <DialogContent>
        <Stack spacing={2}>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}

          {showModel && (
            <Box>
              <AvailableModelSelector
                projectId={projectId}
                value={model}
                onChange={setModel}
                providers={['google']}
                label="AI model"
                size="small"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Currently only Gemini supports the structured-output schema this
                handler needs. Pro is slower but produces tighter output.
              </Typography>
            </Box>
          )}

          {showModel && (
            <Box>
              <TextField
                label="Analysis focus (optional)"
                placeholder="e.g. Focus on B2B SaaS positioning. Ignore travel and personal posts."
                multiline
                rows={3}
                fullWidth
                size="small"
                value={analysisInstructions}
                onChange={(e) => setAnalysisInstructions(e.target.value)}
                inputProps={{
                  maxLength: 1000,
                  'data-testid': `${TESTID}-analysis-instructions`,
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Tell the AI what to emphasize or ignore in this analysis pass.
                Leave blank to skip.
              </Typography>
            </Box>
          )}

          {showMaxPosts && (
            <Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label={maxPostsLabel}
                  size="small"
                  type="number"
                  disabled={allPosts}
                  value={allPosts ? '' : maxPostsNum}
                  onChange={(e) => setMaxPostsNum(Number(e.target.value) || 0)}
                  inputProps={{ min: 1, max: 1000, 'data-testid': `${TESTID}-max-posts` }}
                  sx={{ width: 200 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={allPosts}
                      onChange={(e) => setAllPosts(e.target.checked)}
                      data-testid={`${TESTID}-all-posts-switch`}
                    />
                  }
                  label="All posts"
                />
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {maxPostsHelp}
              </Typography>
            </Box>
          )}

          {shouldShowDateRange && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Date range (optional)
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="From"
                  size="small"
                  type="date"
                  value={scrapeSince}
                  onChange={(e) => setScrapeSince(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 'data-testid': `${TESTID}-scrape-since` }}
                  sx={{ width: 180 }}
                />
                <TextField
                  label="To"
                  size="small"
                  type="date"
                  value={scrapeUntil}
                  onChange={(e) => setScrapeUntil(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 'data-testid': `${TESTID}-scrape-until` }}
                  sx={{ width: 180 }}
                />
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                LinkedIn / BrightData only. Leave blank to fetch the most recent
                posts. Inclusive on both ends.
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogFooter
        alignment="right"
        secondaryAction={{
          label: 'Cancel',
          onClick: onClose,
          disabled: submitting,
          'data-testid': `${TESTID}-cancel`,
        }}
        primaryAction={{
          label: submitting ? 'Running…' : confirmLabel ?? 'Run',
          onClick: handleConfirm,
          disabled: submitting,
          loading: submitting,
          'data-testid': `${TESTID}-confirm`,
        }}
      />
    </BaseDialog>
  );
};

export default ConfirmActionDialog;
